/**
 * Ask Hobbes History API service for fetching and managing
 * persisted conversation history.
 */

import apiClient from './client';
import type { ApiResponse } from './client';
import type { OrgAskEvidenceItem } from './orgAsk';

export interface AskHobbesHistoryItem {
  id: string;
  organization_id: string;
  user_id: string;
  chat_id?: string | null;
  turn_index?: number | null;
  prompt: string;
  response_content?: string | null;
  evidence?: OrgAskEvidenceItem[] | null;
  created_at: string;
}

export interface AskHobbesHistoryListResponse {
  items: AskHobbesHistoryItem[];
  total: number;
}

export interface AskHobbesChatItem {
  id: string;
  organization_id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  latest_prompt?: string | null;
}

export interface AskHobbesChatListResponse {
  items: AskHobbesChatItem[];
  total: number;
}

export interface AskHobbesChatDetailResponse {
  chat: AskHobbesChatItem;
  turns: AskHobbesHistoryItem[];
}

class AskHobbesHistoryService {
  async getChats(
    organizationId: string,
    options?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<AskHobbesChatListResponse>> {
    return apiClient.get<AskHobbesChatListResponse>(
      '/api/session-analytics/org/ask-chats',
      {
        organizationId,
        limit: options?.limit ?? 50,
        offset: options?.offset ?? 0,
      },
    );
  }

  async getChat(chatId: string): Promise<ApiResponse<AskHobbesChatDetailResponse>> {
    return apiClient.get<AskHobbesChatDetailResponse>(
      `/api/session-analytics/org/ask-chats/${chatId}`,
    );
  }

  async renameChat(chatId: string, title: string): Promise<ApiResponse<AskHobbesChatItem>> {
    return apiClient.patch<AskHobbesChatItem>(
      `/api/session-analytics/org/ask-chats/${chatId}`,
      { title },
    );
  }

  async deleteChat(chatId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return apiClient.delete<{ ok: boolean }>(
      `/api/session-analytics/org/ask-chats/${chatId}`,
    );
  }

  /**
   * Fetch paginated Ask Hobbes conversation history for an organization.
   */
  async getHistory(
    organizationId: string,
    options?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<AskHobbesHistoryListResponse>> {
    return apiClient.get<AskHobbesHistoryListResponse>(
      '/api/session-analytics/org/ask-history',
      {
        organizationId,
        limit: options?.limit ?? 50,
        offset: options?.offset ?? 0,
      },
    );
  }

  /**
   * Delete a single history entry.
   */
  async deleteEntry(entryId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return apiClient.delete<{ ok: boolean }>(
      `/api/session-analytics/org/ask-history/${entryId}`,
    );
  }
}

const askHobbesHistoryService = new AskHobbesHistoryService();
export default askHobbesHistoryService;
